import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { MessageCircle, Award, Target, Star, Zap, Trophy, Heart, Mail } from 'lucide-react';

const Home = () => {
  const fadeUp = {
    initial: { opacity: 0, y: 40 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.8, ease: 'easeOut' },
  };

  const services = [
    {
      icon: Award,
      title: 'Acompanhamento Nutricional',
      description: 'Avaliação completa, bioimpedância, estratégia alimentar personalizada.',
      image: 'https://images.unsplash.com/photo-1758652561808-a0d9337fd57a?w=800&q=85',
    },
    {
      icon: Target,
      title: 'Acompanhamento Trimestral',
      description: 'Consulta, plano alimentar individualizado e suporte via WhatsApp por 3 meses.',
      image: 'https://images.unsplash.com/photo-1502783167913-8e718e555a06?w=800&q=85',
    },
    {
      icon: Star,
      title: 'Acompanhamento Semestral',
      description: 'Tudo do trimestral com reavaliações quinzenais e dossiê de evolução semestral.',
      image: 'https://images.unsplash.com/photo-1699214493925-54c7e2fc8ef6?w=800&q=85',
    },
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        <div
          data-editor-key="images.hero"
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(/images/hero-training.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        >
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-[#0A0A0A]/60 to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: 'easeOut' }}
          >
            <h1 data-editor-key="identity.nameUpper" className="text-5xl md:text-7xl lg:text-8xl font-black tracking-tighter uppercase mb-6 bg-clip-text text-transparent bg-gradient-to-r from-white via-gray-300 to-gray-500">
              Daniel Aron
            </h1>
            <p data-editor-key="identity.title" className="text-2xl md:text-3xl font-semibold tracking-tight text-[#C0C0C0] mb-4">
              Nutricionista & Personal Trainer
            </p>
            <p data-editor-key="identity.heroDescription" className="text-base md:text-lg font-light leading-relaxed tracking-wide text-[#E5E5E5] max-w-2xl mx-auto mb-12">
              Transforme seu corpo com estratégia, ciência e acompanhamento profissional.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
              <a
                href="https://wa.me/5521967611301"
                target="_blank"
                rel="noopener noreferrer"
                className="rounded-full bg-white text-black font-bold uppercase tracking-widest px-8 py-4 hover:scale-105 transition-transform duration-300 shadow-[0_0_20px_rgba(255,255,255,0.3)] flex items-center gap-2"
                data-testid="hero-cta-schedule"
              >
                <MessageCircle size={20} />
                Agendar protocolo
              </a>
              <Link
                to="/contato"
                className="rounded-full border border-white/20 bg-transparent text-white font-medium uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-black transition-colors duration-300 flex items-center gap-2"
                data-testid="hero-cta-whatsapp"
              >
                <Mail size={20} />
                Contato
              </Link>
            </div>
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-12 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
            <div className="w-1 h-3 bg-white/50 rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* About Preview Section */}
      <section className="py-24 md:py-32" data-testid="about-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div {...fadeUp}>
              <div className="relative h-[500px] overflow-hidden rounded-lg">
                <img
                  data-editor-key="images.portraitHome"
                  src="/images/daniel-portrait-3.jpg"
                  alt="Daniel Aron"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent opacity-60" />
              </div>
            </motion.div>

            <motion.div {...fadeUp} transition={{ delay: 0.2 }}>
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-6">
                Sobre mim
              </h2>
              <p data-editor-key="identity.aboutPreview" className="text-base md:text-lg font-light leading-loose tracking-wide text-[#E5E5E5] mb-6">
                Daniel Aron é nutricionista e profissional de educação física, especializado em
                transformação corporal, emagrecimento e hipertrofia.
              </p>
              <p className="text-base md:text-lg font-light leading-loose tracking-wide text-[#E5E5E5] mb-8">
                Com uma abordagem baseada em ciência e prática de treino, Daniel ajuda seus pacientes
                a conquistarem resultados reais através de estratégias nutricionais personalizadas.
              </p>
              <Link
                to="/sobre"
                className="inline-block rounded-full border border-white/20 bg-transparent text-white font-medium uppercase tracking-widest px-8 py-4 hover:bg-white hover:text-black transition-colors duration-300"
                data-testid="about-learn-more"
              >
                Saiba mais
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section data-editor-key="services" className="py-24 md:py-32 bg-[#121212]" data-testid="services-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="text-center mb-16">
            <h2 className="text-3xl md:text-5xl font-bold tracking-tight uppercase mb-4">
              Serviços
            </h2>
            <p className="text-base md:text-lg font-light leading-relaxed tracking-wide text-[#A3A3A3] max-w-2xl mx-auto">
              Estratégias personalizadas para seus objetivos
            </p>
          </motion.div>

          <div className={`grid grid-cols-1 gap-8 ${
            services.length === 1 ? 'max-w-md mx-auto' :
            services.length === 2 ? 'md:grid-cols-2 max-w-3xl mx-auto' :
            services.length === 3 ? 'md:grid-cols-3' :
            'sm:grid-cols-2 lg:grid-cols-4'
          }`}>
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                {...fadeUp}
                transition={{ delay: index * 0.1 }}
                className="group relative overflow-hidden bg-[#0A0A0A] border border-white/5 hover:border-white/20 transition-all duration-500"
                data-testid={`service-card-${index}`}
              >
                <div className="relative h-64 overflow-hidden">
                  <img
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] to-transparent" />
                </div>
                <div className="p-8">
                  <service.icon className="text-[#C0C0C0] mb-4" size={32} strokeWidth={1.5} />
                  <h3 className="text-2xl font-semibold tracking-normal mb-4">{service.title}</h3>
                  <p className="text-[#A3A3A3] leading-relaxed">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </div>

          <motion.div {...fadeUp} className="text-center mt-12">
            <Link
              to="/consultas"
              className="inline-block rounded-full bg-white text-black font-bold uppercase tracking-widest px-8 py-4 hover:scale-105 transition-transform duration-300"
              data-testid="services-view-all"
            >
              Ver todos os serviços
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32" data-testid="cta-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div {...fadeUp}>
            <h2 className="text-4xl md:text-6xl font-bold tracking-tight uppercase mb-6">
              Pronto para transformar seu físico e sua saúde?
            </h2>
            <p className="text-base md:text-lg font-light leading-relaxed tracking-wide text-[#A3A3A3] mb-12 max-w-2xl mx-auto">
              Agende seu protocolo e comece sua jornada de transformação com acompanhamento profissional e
              estratégias personalizadas.
            </p>
            <a
              href="https://wa.me/5521967611301"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-3 rounded-full bg-white text-black font-bold uppercase tracking-widest px-8 py-4 hover:scale-105 transition-transform duration-300 shadow-[0_0_20px_rgba(255,255,255,0.3)]"
              data-testid="cta-schedule"
            >
              <MessageCircle size={20} />
              Agendar protocolo
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;